import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@material-ui/core';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import ReplayIcon from '@material-ui/icons/Replay';
import CloseIcon from '@material-ui/icons/Close';

// Replay animado do PERCURSO REAL (rasto GPS) sobre o mapa Google real.
// Recebe o trail já carregado no Redux (actualTrail.trail: [{lat,lng,speed,timestamp}])
// e a instância google.maps.Map (mapRef.current). Gere o marcador do veículo e a
// polyline "já percorrida" de forma imperativa (como o antigo RouteReplay fazia
// no Mapbox). Controlos: play/pausa, velocidade, barra de tempo.
//
// Playback NORMALIZADO: a viagem inteira reproduz-se em ~BASE_SECONDS a 1× (a
// velocidade multiplica), mas a POSIÇÃO é interpolada pelos TIMESTAMPS reais —
// por isso o veículo abranda onde esteve parado. Fallback a índice quando não
// há timestamps fiáveis.
const SPEEDS = [1, 2, 4, 8, 16];
const BASE_SECONDS = 30;

function RouteReplayGoogle({ trail, map, lang = 'pt', vehicleIconUrl, onClose }) {
  const pt = lang === 'pt';
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1 ao longo da viagem
  const [speed, setSpeed] = useState(4);
  const animRef = useRef(null);
  const lastTsRef = useRef(0);
  const markerRef = useRef(null);
  const traveledRef = useRef(null);
  const speedRef = useRef(speed);
  const playingRef = useRef(playing);
  useEffect(() => { speedRef.current = speed; }, [speed]);
  useEffect(() => { playingRef.current = playing; }, [playing]);

  const pts = useMemo(() => (Array.isArray(trail) ? trail.filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng)) : []), [trail]);

  // Timestamps → base temporal para interpolação realista. Fallback: índice.
  const times = useMemo(() => {
    const ms = pts.map(p => { const t = p.timestamp ? new Date(p.timestamp).getTime() : NaN; return Number.isFinite(t) ? t : NaN; });
    const ok = ms.every(Number.isFinite) && ms.length > 1 && ms[ms.length - 1] > ms[0];
    return ok ? ms : null;
  }, [pts]);

  // Posição (lat/lng/speed/time) para um dado progress 0..1.
  const sampleAt = useCallback((pr) => {
    if (pts.length === 0) return null;
    if (pts.length === 1) return { ...pts[0], t: pts[0].timestamp ? new Date(pts[0].timestamp).getTime() : Date.now() };
    let idx, frac;
    if (times) {
      const t0 = times[0], tN = times[times.length - 1];
      const target = t0 + pr * (tN - t0);
      // procura o troço [i, i+1] que contém target
      let i = 0;
      while (i < times.length - 1 && times[i + 1] < target) i++;
      idx = i; frac = (target - times[i]) / Math.max(1, (times[i + 1] - times[i]));
    } else {
      const f = pr * (pts.length - 1);
      idx = Math.floor(f); frac = f - idx;
    }
    const a = pts[idx], b = pts[Math.min(idx + 1, pts.length - 1)];
    const lat = a.lat + (b.lat - a.lat) * frac;
    const lng = a.lng + (b.lng - a.lng) * frac;
    const spd = a.speed != null ? a.speed : (b.speed != null ? b.speed : null);
    const t = times ? times[0] + pr * (times[times.length - 1] - times[0]) : (a.timestamp ? new Date(a.timestamp).getTime() : Date.now());
    return { lat, lng, speed: spd, t, idx };
  }, [pts, times]);

  // Cria marcador + polyline "percorrida" e ajusta a câmara ao percurso.
  useEffect(() => {
    if (!map || !window.google || pts.length === 0) return undefined;
    const g = window.google.maps;
    // Ícone: o veículo real da OT (tipo/tanker/etc.) quando disponível; senão
    // um círculo laranja como fallback.
    const icon = vehicleIconUrl
      ? { url: vehicleIconUrl, scaledSize: new g.Size(40, 40), anchor: new g.Point(20, 20) }
      : { path: g.SymbolPath.CIRCLE, scale: 7, fillColor: '#f97316', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2.5 };
    markerRef.current = new g.Marker({
      map,
      position: { lat: pts[0].lat, lng: pts[0].lng },
      zIndex: 9999,
      icon,
    });
    traveledRef.current = new g.Polyline({
      map, path: [{ lat: pts[0].lat, lng: pts[0].lng }],
      strokeColor: '#f97316', strokeOpacity: 0.95, strokeWeight: 5, zIndex: 9998,
    });
    // fit bounds
    if (pts.length > 1) {
      const b = new g.LatLngBounds();
      pts.forEach(p => b.extend({ lat: p.lat, lng: p.lng }));
      map.fitBounds(b, 80);
    }
    return () => {
      if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }
      if (traveledRef.current) { traveledRef.current.setMap(null); traveledRef.current = null; }
    };
  }, [map, pts]);

  // Aplica o progress ao marcador + polyline percorrida.
  const applyProgress = useCallback((pr) => {
    const s = sampleAt(pr);
    if (!s || !markerRef.current) return;
    markerRef.current.setPosition({ lat: s.lat, lng: s.lng });
    if (traveledRef.current) {
      const path = [];
      const upTo = s.idx != null ? s.idx : 0;
      for (let i = 0; i <= upTo; i++) path.push({ lat: pts[i].lat, lng: pts[i].lng });
      path.push({ lat: s.lat, lng: s.lng });
      traveledRef.current.setPath(path);
    }
  }, [sampleAt, pts]);

  useEffect(() => { applyProgress(progress); }, [progress, applyProgress]);

  // Loop de animação (normalizado: BASE_SECONDS a 1×).
  const tick = useCallback((ts) => {
    if (!playingRef.current) return;
    const dt = ts - (lastTsRef.current || ts);
    lastTsRef.current = ts;
    setProgress(prev => {
      const next = prev + (dt / (BASE_SECONDS * 1000)) * speedRef.current;
      if (next >= 1) { setPlaying(false); return 1; }
      return next;
    });
    animRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (playing) {
      lastTsRef.current = performance.now();
      animRef.current = requestAnimationFrame(tick);
    }
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [playing, tick]);

  const cur = sampleAt(progress);
  const timeStr = cur?.t ? new Date(cur.t).toLocaleTimeString(pt ? 'pt-PT' : 'en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
  const kmh = cur?.speed != null ? Math.round(cur.speed) : null;
  const atEnd = progress >= 1;

  const togglePlay = () => { if (atEnd) setProgress(0); setPlaying(p => !p); };
  const cycleSpeed = () => setSpeed(s => SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length]);
  const handleClose = () => { setPlaying(false); if (animRef.current) cancelAnimationFrame(animRef.current); onClose && onClose(); };

  if (pts.length < 2) {
    return (
      <Box style={panelStyle}>
        <Typography style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{pt ? 'Sem dados de percurso para reproduzir.' : 'No trail data to replay.'}</Typography>
        <IconButton size="small" onClick={handleClose}><CloseIcon style={{ fontSize: 16 }} /></IconButton>
      </Box>
    );
  }

  return (
    <Box style={{ ...panelStyle, width: 520, gap: 10 }}>
      <IconButton size="small" onClick={togglePlay}
        style={{ background: playing ? '#fee2e2' : '#dcfce7', width: 34, height: 34, flexShrink: 0 }}>
        {atEnd ? <ReplayIcon style={{ fontSize: 18, color: '#f97316' }} />
          : playing ? <PauseIcon style={{ fontSize: 18, color: '#ef4444' }} />
          : <PlayArrowIcon style={{ fontSize: 18, color: '#22c55e' }} />}
      </IconButton>

      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <input type="range" min={0} max={1000} value={Math.round(progress * 1000)}
          onChange={e => { setPlaying(false); setProgress(Number(e.target.value) / 1000); }}
          style={{ width: '100%', height: 4, accentColor: '#f97316', cursor: 'pointer' }} />
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography style={{ fontSize: '0.62rem', color: '#475569', fontWeight: 600 }}>{timeStr}</Typography>
          <Typography style={{ fontSize: '0.62rem', color: kmh != null ? '#0f172a' : '#cbd5e1', fontWeight: 700 }}>
            {kmh != null ? `${kmh} km/h` : '—'}
          </Typography>
        </Box>
      </Box>

      <Tooltip title={pt ? 'Velocidade de reprodução' : 'Playback speed'} arrow>
        <IconButton size="small" onClick={cycleSpeed} style={{ width: 40, height: 34, background: '#f1f5f9', flexShrink: 0, borderRadius: 8 }}>
          <Typography style={{ fontSize: '0.68rem', fontWeight: 800, color: '#475569' }}>{speed}×</Typography>
        </IconButton>
      </Tooltip>

      <IconButton size="small" onClick={handleClose} style={{ width: 32, height: 34, flexShrink: 0 }}>
        <CloseIcon style={{ fontSize: 16, color: '#94a3b8' }} />
      </IconButton>
    </Box>
  );
}

const panelStyle = {
  position: 'absolute', bottom: 70, left: '50%', transform: 'translateX(-50%)', zIndex: 760,
  background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(10px)', borderRadius: 12,
  padding: '8px 14px', boxShadow: '0 8px 28px rgba(15,23,42,0.2)', border: '1px solid rgba(15,23,42,0.08)',
  maxWidth: '92vw', display: 'flex', alignItems: 'center', gap: 8,
};

export default RouteReplayGoogle;
